import { api } from './api'
import { WaSettings, WaRateLimitConfig, WaRateLimitStatus, WaBusinessHoursStatus } from '@/types'

export interface UpdateWaSettingsRequest {
  timezone?: string
  rateLimitConfig?: WaRateLimitConfig
  autoReplyEnabled?: boolean
  autoReplyMessage?: string | null
  businessHoursOnly?: boolean
  businessHoursStart?: number
  businessHoursEnd?: number
  businessDays?: number[]
}

export const waSettingsService = {
  // User Settings
  async getSettings(): Promise<WaSettings> {
    const response = await api.get<{ success: boolean; data: WaSettings }>('/whatsapp/settings')
    return response.data.data
  },

  async updateSettings(data: UpdateWaSettingsRequest): Promise<WaSettings> {
    const response = await api.put<{ success: boolean; data: WaSettings }>('/whatsapp/settings', data)
    return response.data.data
  },

  async resetSettings(): Promise<WaSettings> {
    const response = await api.post<{ success: boolean; data: WaSettings }>('/whatsapp/settings/reset')
    return response.data.data
  },

  // Account-specific Settings
  async getAccountSettings(accountId: number): Promise<WaSettings | null> {
    try {
      const response = await api.get<{ success: boolean; data: WaSettings | null }>(
        `/whatsapp/settings/account/${accountId}`
      )
      return response.data.data
    } catch {
      return null
    }
  },

  async updateAccountSettings(accountId: number, data: UpdateWaSettingsRequest): Promise<WaSettings> {
    const response = await api.put<{ success: boolean; data: WaSettings }>(
      `/whatsapp/settings/account/${accountId}`,
      data
    )
    return response.data.data
  },

  async deleteAccountSettings(accountId: number): Promise<void> {
    await api.delete(`/whatsapp/settings/account/${accountId}`)
  },

  // Auto-Reply
  async getAutoReplyConfig(accountId?: number): Promise<{ enabled: boolean; message: string | null }> {
    const response = await api.get<{
      success: boolean
      data: { enabled: boolean; message: string | null }
    }>('/whatsapp/settings/auto-reply', { params: { accountId } })
    return response.data.data
  },

  async updateAutoReply(data: { enabled: boolean; message?: string }, accountId?: number): Promise<WaSettings> {
    const response = await api.put<{ success: boolean; data: WaSettings }>(
      '/whatsapp/settings/auto-reply',
      { ...data, accountId }
    )
    return response.data.data
  },

  // Business Hours
  async checkBusinessHours(accountId?: number): Promise<WaBusinessHoursStatus> {
    const response = await api.get<{ success: boolean; data: WaBusinessHoursStatus }>(
      '/whatsapp/settings/business-hours/status',
      { params: { accountId } }
    )
    return response.data.data
  },

  async updateBusinessHours(
    data: {
      enabled: boolean
      startHour: number
      endHour: number
      days: number[]
    },
    accountId?: number
  ): Promise<WaSettings> {
    const response = await api.put<{ success: boolean; data: WaSettings }>(
      '/whatsapp/settings/business-hours',
      { ...data, accountId }
    )
    return response.data.data
  },

  // Rate Limiting
  async checkRateLimit(accountId?: number): Promise<WaRateLimitStatus> {
    const response = await api.get<{ success: boolean; data: WaRateLimitStatus }>(
      '/whatsapp/settings/rate-limits/status',
      { params: { accountId } }
    )
    return response.data.data
  },

  async updateRateLimits(config: WaRateLimitConfig, accountId?: number): Promise<WaSettings> {
    const response = await api.put<{ success: boolean; data: WaSettings }>(
      '/whatsapp/settings/rate-limit',
      { rateLimitConfig: config, accountId }
    )
    return response.data.data
  },
}
